import { ReactNode } from "react";
import { Stack, Text, XStack, YStack } from "tamagui";
import { RiBankCardLine, RiCheckLine } from "react-icons/ri";
import { colors } from "@styles/colors";

// 與 Kiosk PaymentOptions 同一套切版（卡別徽章 / 藍框選中 / 打勾），
// 抽成共用元件供正式結帳（PayMethodSwitchMenu）使用；正式頁不依賴 kioskTheme。
const ink = "#0f1a35";
const grey = "#6b7280";
const line = "#eceff5";

/** 卡別/付款方式左側徽章外框（統一尺寸，讓各列 icon 對齊）。 */
export const LeadingBadge = ({ children, bg = "#fff" }: { children: ReactNode; bg?: string }) => (
  <Stack width={42} height={28} borderRadius={6} borderWidth={1} borderColor="#e6e9f0" backgroundColor={bg} ai="center" jc="center">
    {children}
  </Stack>
);

/** 依 bincode 首碼判斷卡別，回傳對應 logo 徽章（4=Visa、5/2=Mastercard、3=JCB，其餘泛用卡）。 */
export const CardBrand = ({ bincode }: { bincode?: string }) => {
  const d = (bincode || "")[0];
  if (d === "4")
    return (
      <LeadingBadge>
        <Text fontSize={13} fontWeight="800" fontStyle="italic" color="#1a1f71" letterSpacing={-0.3}>
          VISA
        </Text>
      </LeadingBadge>
    );
  if (d === "5" || d === "2")
    return (
      <LeadingBadge>
        <XStack ai="center">
          <Stack width={13} height={13} borderRadius={7} backgroundColor="#eb001b" />
          <Stack width={13} height={13} borderRadius={7} backgroundColor="#f79e1b" ml={-6} opacity={0.92} />
        </XStack>
      </LeadingBadge>
    );
  if (d === "3")
    return (
      <LeadingBadge>
        <Text fontSize={11} fontWeight="900" color="#0b6cb3" letterSpacing={-0.2}>
          JCB
        </Text>
      </LeadingBadge>
    );
  return (
    <LeadingBadge>
      <RiBankCardLine size={19} color="#8a93a6" />
    </LeadingBadge>
  );
};

/** 單列選項（左側徽章 + 標題/副標；選中以藍框表示，選中時右側顯示打勾）。 */
export const PaymentOptionRow = ({
  title,
  sub,
  selected,
  onPress,
  leading,
}: {
  title: string;
  sub?: string;
  selected: boolean;
  onPress: () => void;
  leading?: ReactNode;
}) => (
  <XStack
    ai="center"
    jc="space-between"
    px={14}
    py={13}
    gap={12}
    borderWidth={1.5}
    borderColor={selected ? colors.primary : line}
    borderRadius={12}
    backgroundColor={selected ? "#f2f8ff" : "#fff"}
    onPress={onPress}
    pressStyle={{ opacity: 0.75 }}
    style={{ cursor: "pointer" }}>
    <XStack ai="center" gap={12} flex={1}>
      {leading}
      <YStack flex={1}>
        <Text fontSize={16} fontWeight="700" color={ink}>
          {title}
        </Text>
        {sub ? (
          <Text fontSize={12.5} color={grey} mt={2}>
            {sub}
          </Text>
        ) : null}
      </YStack>
    </XStack>
    {selected ? <RiCheckLine size={19} color={colors.primary} /> : null}
  </XStack>
);

/** 虛線框「＋ 新增信用卡」列。 */
export const AddCardRow = ({ onPress }: { onPress: () => void }) => (
  <XStack
    ai="center"
    jc="center"
    gap={6}
    px={14}
    py={14}
    borderWidth={1.5}
    borderColor={line}
    borderStyle="dashed"
    borderRadius={12}
    onPress={onPress}
    pressStyle={{ opacity: 0.7 }}
    style={{ cursor: "pointer" }}>
    <Text fontSize={15.5} fontWeight="700" color={colors.primary}>
      ＋ 新增信用卡
    </Text>
  </XStack>
);
